import * as moment from "moment";
import {
	InvoiceEventApiDto,
	AccountingCommonUpdatedEventDataApiDto,
	AccountingCommonUpdatedFieldChangeDataDto,
	AccountingCommonUpdatedItemChangeDataDto
} from "../../../api/model/rest";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";
import InvoiceEvent, {InvoiceEventTypeEnum} from "./InvoiceEvent";
import {AccountingCommonUpdatedEventHelper, AccountingCommonFieldDifferenceType} from "./AccountingCommonUpdatedEventHelper";

/**
 * Invoice updated event model.
 */
export default class InvoiceUpdatedEvent extends InvoiceEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {InvoiceEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} creator entity which created the event.
	 * @param {moment.Moment} creationDateTime creation date of event.
	 * @param {AccountingCommonUpdatedEventData} data invoice updated event data.
	 */
	constructor(public readonly id: string,
		public readonly type: InvoiceEventTypeEnum,
		public readonly creator: AuthenticatedEntity,
		public readonly creationDateTime: moment.Moment,
		public readonly data: AccountingCommonUpdatedEventData) {
		super(id, type, creator, creationDateTime);
	}

	public static fromJson(json: InvoiceEventApiDto): InvoiceUpdatedEvent {
		const data = AccountingCommonUpdatedEventData.fromJson(json.data as AccountingCommonUpdatedEventDataApiDto);
		const creator = AuthenticatedEntity.fromJson(json.creator);
		return new InvoiceUpdatedEvent(json.id, InvoiceEventTypeEnum.InvoiceUpdated, creator,
			moment(json.creationDateTime), data);
	}

	getIcon() {
		return Icon.Status;
	}

	getActivityType(): string {
		return `INVOICE UPDATED`;
	}

	getActivityMessage(): string {
		return `Invoice updated`
	}
}

export class AccountingCommonUpdatedEventData {
	constructor(public readonly fieldChanges: AccountingCommonUpdatedFieldChangeData[],
		public readonly itemChanges: AccountingCommonUpdatedItemChangeData[]) {}

	public static fromJson(json: AccountingCommonUpdatedEventDataApiDto): AccountingCommonUpdatedEventData {
		const fieldChanges = json.fieldChanges.map(AccountingCommonUpdatedFieldChangeData.fromJson);
		const itemChanges = json.itemChanges.map(AccountingCommonUpdatedItemChangeData.fromJson);
		return new AccountingCommonUpdatedEventData(fieldChanges, itemChanges);
	}
}

export class AccountingCommonUpdatedFieldChangeData {
	constructor(public readonly fieldName: string,
		public readonly newValue: string,
		public readonly oldValue: string) {}

	public static fromJson(json: AccountingCommonUpdatedFieldChangeDataDto) {
		const newValue = AccountingCommonUpdatedEventHelper.formatValue(json.newValue, json.fieldName);
		const oldValue = AccountingCommonUpdatedEventHelper.formatValue(json.oldValue, json.fieldName);
		return new AccountingCommonUpdatedFieldChangeData(json.fieldName, newValue, oldValue);
	}

	getFormattedChange(diffType: AccountingCommonFieldDifferenceType = AccountingCommonFieldDifferenceType.CHANGED) {
		return AccountingCommonUpdatedEventHelper.formatChange(this.fieldName, this.oldValue, this.newValue, diffType)
	}
}

export class AccountingCommonUpdatedItemChangeData {
	constructor(public readonly description: string,
		public readonly diffType: AccountingCommonFieldDifferenceType,
		public readonly fieldChanges: AccountingCommonUpdatedFieldChangeData[]) {}

	public static fromJson(json: AccountingCommonUpdatedItemChangeDataDto) {
		const fieldChanges = json.fieldChanges.map(AccountingCommonUpdatedFieldChangeData.fromJson);
		// no diff type for old events - setting CHANGED to keep backward compatibility
		const diffTypeKey = json.diffType as keyof typeof AccountingCommonFieldDifferenceType;
		const diffType = json.diffType ? AccountingCommonFieldDifferenceType[diffTypeKey] : AccountingCommonFieldDifferenceType.CHANGED;
		return new AccountingCommonUpdatedItemChangeData(json.description, diffType, fieldChanges);
	}

	getDescription() {
		return AccountingCommonUpdatedEventHelper.formatDescription(this.description, this.diffType);
	}
}
